import { HTS_READINESS_PILLARS_CONFIG, HtsFlagPriority, HtsInputType, HtsReadinessPillarKey } from '@evaltick/shared';
import type { HtsGroupField } from '@evaltick/shared';
import { HTS_INPUT_TYPE_LABELS } from '@/utils/htsInputTypeLabels';
import type { HtsGroupFieldFormValue } from './htsQuestionFormTypes';

export const NEEDS_OPTIONS = new Set<HtsInputType>([HtsInputType.SELECT, HtsInputType.CHECKBOXES]);

export const isGroupFieldValid = (field: HtsGroupFieldFormValue): boolean =>
  Boolean(field.label?.trim()) &&
  (!NEEDS_OPTIONS.has(field.inputType) || (field.options ?? []).some((o) => o?.trim()));

// Shared by the condition editor's options list and its validity check.
export const isTargetableGroupField = (
  field: HtsGroupFieldFormValue
): field is HtsGroupFieldFormValue & { key: string } =>
  Boolean(field.key?.trim()) && Boolean(field.label?.trim());

const slugifyToCamelCase = (label: string): string => {
  const words = label
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return words
    .map((word, i) => (i === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()))
    .join('');
};

/**
 * A row sub-field's stable identity: label-derived, unique within `usedKeys`
 * (which this adds to), and never regenerated once assigned — a later label
 * edit must not orphan hard-gate conditions or answers that reference it by key.
 */
export const generateGroupFieldKey = (label: string, usedKeys: Set<string>): string => {
  const base = slugifyToCamelCase(label) || 'field';
  let candidate = base;
  let suffix = 2;
  while (usedKeys.has(candidate)) {
    candidate = `${base}${suffix}`;
    suffix += 1;
  }
  usedKeys.add(candidate);
  return candidate;
};

/**
 * Assigns a key to every labelled row field missing one, leaving already-keyed
 * fields untouched; returns null if nothing changed, so callers can skip a
 * redundant form write. Needed because Step 5's hard-gate conditions must
 * reference a field's key before save, though keys are otherwise only assigned then.
 */
export const backfillGroupFieldKeys = (
  rows: HtsGroupFieldFormValue[]
): HtsGroupFieldFormValue[] | null => {
  const needsKey = (row: HtsGroupFieldFormValue) => !row.key?.trim() && Boolean(row.label?.trim());
  if (!rows.some(needsKey)) return null;
  const usedKeys = new Set(rows.map((r) => r.key).filter((k): k is string => Boolean(k?.trim())));
  return rows.map((row) => (needsKey(row) ? { ...row, key: generateGroupFieldKey(row.label, usedKeys) } : row));
};

export const GROUP_SUB_FIELD_TYPE_OPTIONS = Object.values(HtsInputType)
  .filter((value): value is HtsGroupField['inputType'] => value !== HtsInputType.REPEATABLE_GROUP)
  .map((value) => ({ label: HTS_INPUT_TYPE_LABELS[value], value }));

export const READINESS_PILLAR_OPTIONS = HTS_READINESS_PILLARS_CONFIG.filter(
  (p) => p.key !== HtsReadinessPillarKey.INTEGRITY
).map((p) => ({ label: p.label, value: p.key }));

export const HARD_GATE_PRIORITY_OPTIONS = [
  { label: 'Standard', value: HtsFlagPriority.STANDARD },
  { label: 'High', value: HtsFlagPriority.HIGH },
];

export const HTS_CANONICAL_SECTIONS = [
  'Section A – General Information & Pre-requisites',
  'Section B – Character & Background',
  'Section C – Education & Experience',
  'Section D – Readiness Profile',
  'Section E – Self-Awareness',
];
